import { removeDuplicates } from "@utils/array";
import WrangleDefinition from "./definitions/wrangled.json";
import type { ProviderDefinition, ProviderParser, UnparsedFile } from "./types";
import { topLevel } from "./wrangler/path";

let providerCache: [ProviderDefinition, ProviderParser][] | undefined;

function getProviders(): [ProviderDefinition, ProviderParser][] {
  // Definitions are bundled at build time and never change at runtime.
  if (providerCache !== undefined) return providerCache;

  const modules = import.meta.glob("./definitions/*.json", {
    eager: true,
  }) as Record<string, ProviderDefinition>;

  providerCache = Object.values(modules).map((definition) => [
    definition,
    {
      // Top-level folder of each declared path. Must use the same derivation
      // as findDataProvider, or nothing ever matches.
      fileStructure: removeDuplicates(definition.fileStructure.map(topLevel)),
      optionalFileStructure: removeDuplicates((definition.optionalFileStructure ?? []).map(topLevel)),
      // Was misspelled `instuctions`, which left ProviderParser.instructions
      // undefined and made applyExistingTemplates throw. The `as unknown as
      // ProviderParser` cast that hid it is gone.
      instructions: definition.instructions ?? [],
    },
  ]);

  return providerCache;
}

function getProvider(id: string): ProviderDefinition {
  const match = getProviders().find(([def]) => def.id === id);
  if (match === undefined) throw new Error(`Unknown provider id: ${id}`);
  return match[0];
}

function findDataProvider(source: UnparsedFile[]): {
  provider: ProviderDefinition;
  providerParser: ProviderParser;
  relevance: number;
} {
  const MIN_RELEVANCE = 0.1;
  const found = new Set(source.map((d) => topLevel(d.path.toLowerCase())));

  console.log("Matching top-level file structure against definitions:", Array.from(found));

  let best: {
    provider: ProviderDefinition;
    providerParser: ProviderParser;
    relevance: number;
  } | null = null;

  for (const [provider, parser] of getProviders()) {
    const expected = new Set(parser.fileStructure.map((d) => d.toLowerCase()));
    if (expected.size === 0) continue;

    // Jaccard: the old score divided only by |found|, so exports with many
    // top-level folders were penalised and tiny definitions scored 1.0.
    const intersection = Array.from(expected).filter((d) => found.has(d)).length;
    const union = expected.size + found.size - intersection;
    const relevance = union === 0 ? 0 : intersection / union;

    console.log(`${provider.name}: ${Math.round(relevance * 100)}% (${intersection}/${union})`);

    if (relevance > MIN_RELEVANCE && relevance > (best?.relevance ?? 0)) {
      best = { provider, providerParser: parser, relevance };
    }
  }

  if (best === null) {
    console.log("No suitable definition found. Setting id to wrangled");
    return {
      provider: WrangleDefinition,
      providerParser: {
        fileStructure: [],
        optionalFileStructure: [],
        instructions: [],
      },
      // Was 1, which reported a perfect match for "no match".
      relevance: 0,
    };
  }

  console.log(`Definition is ${best.provider.name}`);
  return best;
}

export { findDataProvider, getProvider, getProviders };
